import React, { useEffect, useState, useContext } from "react"
import { createPortal } from "react-dom"
import { HelveticaBold } from "common/typographies.js"
import { Stack, Button, Box, TextField, Typography } from "@mui/material"

import i18n from "i18n"
import MedicionesDataGrid from "./detallesMediciones/MedicionesDataGrid"
import PropTypes from "prop-types"
import { useProjectContext } from "context/projects/projectContext"
import OptionsPartDropdown from "./OptionsPartDropdown"
import { useBudget2023Context } from "context/budget2023/Budget2023Context"
import { useSnackbar } from "notistack"
import ModalDestacarPalabras from "./ModalDestacarPalabras"
import { getCurrency, calcularImporteDeDescomposicion } from "utils/functions"
import { calcPartida } from "utils/partUtils"
import { useAuthContext } from "context/auth/authContext"
import { useWrapperModals } from "context/wrapperModals/WrapperModalsContext"
import { NEW_MODAL_PARTIDA } from "context/types"
import { useModalPartida } from "hooks/modalPartida/useModalPartida"
import LeftFooterGrid from "./LeftFooterGrid"
import useEntitlementGuard from "hooks/useEntitlementGuard"
import { IMPORT_BC3_LINK } from "constants/entitlements"

import { useDecimalFormatter } from "hooks/useDecimalFormatter"

import Budget2023Context from "context/budget2023/Budget2023Context"
import ModalExportBankPrice from "components/Budgeting2023/options/ModalExportBankPrice"

const LeftGridContent = ({
  bancoDePrecios,
  setOpenModalGeneral,
  partidaState,
  handleClick,
  setErrorMessage,
  details,
  setDetails,
  setPartidaState,
  imInUpdate,
  partidaInfo,
  referenciaObject,
  leftFooterRef,
}) => {
  const { user } = useAuthContext()
  const [enableEdit, setEnableEdit] = useState(false)
  const [toggleBenefits, setToggleBenefits] = useState(false)
  const [mediciones, setMediciones] = useState([])
  const [unity, setUnity] = useState("pa")
  const { projectInfo } = useProjectContext()
  const { copyBudgetData, copiedData, marginModal, budgetMode } =
    useBudget2023Context()
  const { bc3Link } = useWrapperModals()
  const { enqueueSnackbar } = useSnackbar()
  const [openModalDestacarPalabras, setOpenModalDestacarPalabras] =
    useState(false)
  const [checkEntitlement, entitlementDialog] = useEntitlementGuard()

  const { roundToDecimals, getUnitFromTotal } = useDecimalFormatter()

  const {
    state,
    dispatchQuantity,
    setQuantityAndRecalculate,
    dispatchCost,
    setCostAndRecalculate,
    setPricePrereducedAndRecalculate,
    dispatchTotalCost,
    setTotalCostAndRecalculate,
    dispatchCostReduction,
    setCostReductionAndRecalculate,
    dispatchPriceReduced,
    dispatchPricePrereduced,
  } = useModalPartida()

  const { checkPartida, updatePartsList } = useContext(Budget2023Context)
  const [openExportBankPrice, setOpenExportBankPrice] = useState(false)

  const exporFirstStep = async () => {
    await checkPartida(partidaInfo.chapterId, partidaInfo.partidaId)
    await updatePartsList(partidaInfo)
    setOpenExportBankPrice(true)
  }

  useEffect(() => {
    if (imInUpdate && partidaInfo) {
      setEnableEdit(true)
      if (partidaInfo.measures.length > 0) {
        setToggleBenefits(true)
      }
      setMediciones(partidaInfo.measures)
      setUnity(partidaInfo.unity)

      let measures_aux = []
      partidaInfo.measures.forEach((m, i) => {
        let measure = {
          description: m.description,
          quantity: m.quantity,
          depth: m.depth,
          width: m.width,
          height: m.height,
          total: m.total,
        }
        measures_aux.push(measure)
      })
      let total = measures_aux.reduce((acc, m) => acc + m.total, 0)

      setTotalDeTotales(total)

      setPartidaState({
        ...partidaState,
        unity: partidaInfo.unity,
        quantity: partidaInfo.quantity,
      })
    } else {
      setEnableEdit(true)
    }
  }, [])

  const importFromBc3Link = () => {
    if (!checkEntitlement(IMPORT_BC3_LINK, i18n.t("importBC3Link.link"))) return

    bc3Link.setProperty({
      open: true,
      from: NEW_MODAL_PARTIDA,
    })
  }

  const handleChange = e => {
    const { name, value } = e.target

    setPartidaState(prevState => ({
      ...prevState,
      [name]: value,
    }))
  }

  const [totalDeTotales, setTotalDeTotales] = useState(0)

  useEffect(() => {
    if (mediciones.length == 1) {
      if (
        mediciones[0].description == 0 &&
        mediciones[0].quantity == 0 &&
        mediciones[0].depth == 0 &&
        mediciones[0].height == 0 &&
        mediciones[0].total == 0
      ) {
        setMediciones([])
      }
    }
  }, [toggleBenefits])

  useEffect(() => {
    if (details.length > 0) {
      let accWithReduction = 0
      let accWithoutReduction = 0
      const newDetails = details.map(d => {
        const priceReduced =
          d.priceReduced !== undefined && d.priceReduced !== null
            ? Number(d.priceReduced)
            : Number(d.price) || 0
        const priceBase = Number(d.price) || 0
        const qty = Number(d.quantity) || 0
        const waste = Number(d.waste) || 0
        accWithReduction += priceReduced * (qty + waste)
        accWithoutReduction += priceBase * (qty + waste)
        d.total = calcularImporteDeDescomposicion(d)
        return d
      })

      dispatchPricePrereduced(accWithReduction)
      setCostAndRecalculate(accWithoutReduction, accWithReduction)

      let priceMaterial = newDetails.reduce((acc, m) => {
        m.type == "material" ? (acc += parseFloat(m.total)) : acc
        return acc
      }, 0)
      let priceManoDeObra = newDetails.reduce((acc, m) => {
        m.type == "manodeobra" ? (acc += parseFloat(m.total)) : acc
        return acc
      }, 0)
      let priceOther = newDetails.reduce((acc, m) => {
        m.type != "material" && m.type != "manodeobra"
          ? (acc += parseFloat(m.total))
          : acc
        return acc
      }, 0)

      let newPart = partidaState
      newPart = calcPartida(
        partidaState,
        "price",
        accWithoutReduction,
        marginModal.formula,
      )

      setPartidaState({
        ...newPart,
        priceMaterial: priceMaterial,
        priceManoDeObra: priceManoDeObra,
        priceOther: priceOther,
      })
    }
  }, [details])

  function getTotal(medicion) {
    const q = medicion.quantity || 0
    const d = medicion.depth || 0
    const w = medicion.width || 0
    const h = medicion.height || 0
    if (q === 0 && d === 0 && w === 0 && h === 0) return 0
    return (
      (q !== 0 ? q : 1) *
      (d !== 0 ? d : 1) *
      (w !== 0 ? w : 1) *
      (h !== 0 ? h : 1)
    )
  }

  useEffect(() => {
    let measures_aux = mediciones.map(m => {
      const measure = {
        description: m.description,
        quantity: m.quantity != null ? parseFloat(m.quantity) : null,
        depth: m.depth != null ? parseFloat(m.depth) : null,
        width: m.width != null ? parseFloat(m.width) : null,
        height: m.height != null ? parseFloat(m.height) : null,
      }

      // Si viene el total y no se editó ningún componente, lo mantenemos
      const original = m
      const edited =
        measure.description !== original.description ||
        measure.quantity !==
          (original.quantity != null ? parseFloat(original.quantity) : null) ||
        measure.depth !==
          (original.depth != null ? parseFloat(original.depth) : null) ||
        measure.width !==
          (original.width != null ? parseFloat(original.width) : null) ||
        measure.height !==
          (original.height != null ? parseFloat(original.height) : null)

      if (original.total != null && !edited) {
        measure.total = original.total
      } else {
        measure.total = getTotal(measure)
      }

      return measure
    })

    let total = measures_aux.reduce((acc, m) => acc + m.total, 0)
    setTotalDeTotales(total)

    if (total > 0) {
      setQuantityAndRecalculate(total)
    }

    if (imInUpdate) {
      if (total == 0) {
        setPartidaState({
          ...partidaState,
          measures: measures_aux,
          unity: unity,
        })
      } else {
        setPartidaState({
          ...partidaState,
          measures: measures_aux,
          unity: unity,
          unidades: total,
          quantity: total,
        })
      }
    } else {
      setPartidaState({
        ...partidaState,
        measures: measures_aux,
        unity: unity,
        unidades: total,
        quantity: total,
      })
    }
  }, [mediciones, unity])

  const isTender = budgetMode === "tender"

  const leftFooterEl =
    leftFooterRef?.current &&
    createPortal(
      <LeftFooterGrid
        unity={unity}
        currency={getCurrency(user["custom:currency"])}
        setUnity={setUnity}
        toggleBenefits={toggleBenefits}
        setToggleBenefits={setToggleBenefits}
        enableEdit={enableEdit}
        setEnableEdit={setEnableEdit}
        bancoDePrecios={bancoDePrecios}
        totalDeTotales={totalDeTotales}
        mediciones={mediciones}
        details={details}
        isTender={isTender}
      />,
      leftFooterRef.current,
    )

  return (
    <>
      <Stack sx={{ height: "100%", position: "relative" }} direction="column">
        <Box
          sx={{
            width: "100%",
            height: "61px",
            backgroundColor: "#F6F7FB",
            borderTopLeftRadius: "10px",
            display: "flex",
            alignItems: "center",
            gap: "2px",
            p: "15px",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body2">
            {`${projectInfo?.name} ${partidaInfo?.ref || ""}`}
          </Typography>
          <Stack
            direction="row"
            sx={{
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {
              <Button
                data-element="importFromBC3LinkBtn"
                variant="outlined"
                size="small"
                onClick={importFromBc3Link}
              >
                +BC3 link
              </Button>
            }
            <Button variant="outlined" size="small" onClick={exporFirstStep}>
              Guardar en BDP
            </Button>
            <Box>
              {bancoDePrecios == "BANCO DE PRECIOS REFERENCIA" ? null : (
                <OptionsPartDropdown
                  bancoDePrecios={bancoDePrecios}
                  setOpenModalGeneral={setOpenModalGeneral}
                  imInUpdate={imInUpdate}
                  partidaInfo={partidaInfo}
                  partidaState={partidaState}
                  setPartidaState={setPartidaState}
                />
              )}
            </Box>
          </Stack>
        </Box>
        <Box
          sx={{
            overflowY: "auto",
            p: "15px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <HelveticaBold
            style={{
              color: "#414141",
              fontSize: "12px",
              marginLeft: "10px",
              marginTop: "0px",
              marginBottom: "10px",
            }}
          >
            {i18n.t("Codigo")}
          </HelveticaBold>
          <TextField
            fullWidth
            size="small"
            onChange={e => {
              handleChange(e)
            }}
            placeholder="Escribe tu Código"
            sx={{
              mb: "10px",
              "& .MuiInputBase-input": {
                fontSize: "14px",
              },
            }}
            name="code"
            value={partidaState.code || ""}
          />
          <HelveticaBold
            style={{
              color: "#414141",
              fontSize: "12px",
              marginLeft: "10px",
              marginTop: "0px",
              marginBottom: "10px",
            }}
          >
            {i18n.t("Descripción corta")}
          </HelveticaBold>
          <TextField
            fullWidth
            multiline
            rows={2}
            inputRef={referenciaObject.referenciaName}
            id="descripcionInput"
            disabled={
              bancoDePrecios == "BANCO DE PRECIOS REFERENCIA" ? true : false
            }
            name="name"
            onChange={e => {
              handleChange(e)
            }}
            placeholder="Escribe tu mensaje"
            value={partidaState.name || ""}
            sx={{
              mb: "10px",
              "& .MuiInputBase-input": {
                fontSize: "14px",
              },
              "& .MuiInputBase-root": {
                padding: "4px 8px",
              },
            }}
          />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="10px"
          >
            <HelveticaBold
              style={{
                color: "#414141",
                fontSize: "12px",
                marginLeft: "10px",
                marginTop: "0px",
                marginBottom: "10px",
              }}
            >
              {i18n.t("Descripción larga")}
            </HelveticaBold>
            <Button
              onClick={() => {
                setOpenModalDestacarPalabras(true)
              }}
              variant="text"
              size="small"
              sx={{
                ml: "auto",
              }}
            >
              {partidaState.comment?.length > 0
                ? i18n.t("Modificar palabras destacadas")
                : i18n.t("Añadir palabras destacadas")}
            </Button>
          </Stack>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flex: 1,
              minHeight: 0,
            }}
          >
            <TextField
              fullWidth
              multiline
              rows={1}
              inputRef={referenciaObject.referenciaDescripcion}
              disabled={
                bancoDePrecios == "BANCO DE PRECIOS REFERENCIA" ? true : false
              }
              onChange={e => {
                handleChange(e)
              }}
              id="descripcionInput"
              onBlur={e => {
                handleChange(e)
              }}
              name="description"
              placeholder="Escribe tu mensaje"
              sx={{
                flex: 1,
                minHeight: 0,
                "& .MuiInputBase-root": {
                  height: "100%",
                  padding: "8px 12px",
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  height: "100% !important",
                  overflowY: "auto !important",
                  resize: "none",
                },
              }}
              value={partidaState.description || ""}
            />
          </Box>

          {toggleBenefits && enableEdit ? (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap="10px"
              >
                <HelveticaBold
                  style={{
                    color: "#414141",
                    fontSize: "12px",
                    marginLeft: "10px",
                    marginTop: "10px",
                    marginBottom: "14px",
                  }}
                >
                  {i18n.t("Detalle de Mediciones")}
                </HelveticaBold>
                <Stack direction="row" alignItems="center" gap="10px" ml="auto">
                  <Button
                    onClick={() => {
                      copyBudgetData({ measures: mediciones })
                      enqueueSnackbar("Mediciones copiadas", {
                        variant: "success",
                        autoHideDuration: 2000,
                      })
                    }}
                    variant="text"
                    size="small"
                  >
                    {i18n.t("Copiar mediciones")}
                  </Button>
                  {copiedData && (
                    <Button
                      onClick={() => {
                        setMediciones(copiedData.measures)
                        copyBudgetData(null)
                      }}
                      variant="text"
                      size="small"
                    >
                      {i18n.t("Pegar mediciones")}
                    </Button>
                  )}
                </Stack>
              </Stack>
              <MedicionesDataGrid
                mediciones={mediciones}
                setMediciones={setMediciones}
                isReadOnly={isTender}
              />
            </Box>
          ) : null}
        </Box>
      </Stack>
      {leftFooterEl}

      <ModalDestacarPalabras
        open={openModalDestacarPalabras}
        setOpen={setOpenModalDestacarPalabras}
        partidaState={partidaState}
        setPartidaState={setPartidaState}
      />

      {openExportBankPrice && (
        <ModalExportBankPrice
          open={openExportBankPrice}
          setOpen={setOpenExportBankPrice}
        />
      )}

      {entitlementDialog}
    </>
  )
}

LeftGridContent.propTypes = {
  partidaState: PropTypes.object,
  setPartidaState: PropTypes.func,
  imInUpdate: PropTypes.bool,
  partidaInfo: PropTypes.object,
  referenciaObject: PropTypes.object,
  details: PropTypes.array,
  setDetails: PropTypes.func,
  setErrorMessage: PropTypes.func,
  handleClick: PropTypes.func,
  setOpenModalGeneral: PropTypes.func,
  bancoDePrecios: PropTypes.string,
  leftFooterRef: PropTypes.object,
}

export default LeftGridContent
